import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { NgForm } from '@angular/forms';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { Router } from '@angular/router';
import { Agencies } from 'app/models/agencies';
import { AgenciesService } from 'app/services/agencies/agencies.service';
import { AwscontrolService } from 'app/services/aws/awscontrol.service';
import { Location } from '@angular/common';
import Swal from 'sweetalert2';
import { HttpErrorResponse } from '@angular/common/http';
import { FormControl } from '@angular/forms';

import { AGENCES_SERVICES } from "./../classesTs/agences-services";
import { MATERIAUX } from "./../classesTs/Materiax";
import { VILLES } from "./../classesTs/Ville";

interface Role {
    value: string;
    viewValue: string;
}

interface LanguageOption {
    code: string;
    label: string;
    checked: boolean;
}

@Component({
    selector: 'app-ajouter-agencie',
    templateUrl: './ajouter-agencie.component.html',
    styleUrls: ['./ajouter-agencie.component.scss']
})
export class AjouterAgencieComponent implements OnInit {
    @ViewChild('agencyForm') public currentForm!: NgForm;
    @ViewChild('fileInput', { static: false }) fileInput!: ElementRef;
    @ViewChild('descriptionTextarea', { static: false }) descriptionTextarea!: ElementRef;
    @ViewChild('descriptionArabTextarea', { static: false }) descriptionArabTextarea!: ElementRef;
    @ViewChild('descriptionAnglaisTextarea', { static: false }) descriptionAnglaisTextarea!: ElementRef;

    agencieData: any = {};
    isLoading = false;
    isUploadingLogo = false;
    isLogoChanged = false;
    public uploadedImages: string[] = [];
    public currentImage: string | null = null;
    selectedFiles1!: FileList;

    // Reel video properties
    selectedReelFile: File | null = null;
    selectedReelFileName: string = '';
    isUploadingReel = false;
    reelFileChanged = false;
    reelUploadedUrl: string | null = null;

    subdomainPreview: string = '';

    // Langues : tableau d'objets avec 'checked' (comme dans modifier)
    allLanguages: LanguageOption[] = [
        { code: 'FR', label: 'Français', checked: true }, // par défaut FR coché
        { code: 'AR', label: 'العربية', checked: false },
        { code: 'EN', label: 'English', checked: false }
    ];

    get hasAnyLanguageSelected(): boolean {
        return this.allLanguages.some(l => l.checked);
    }

    roles: Role[] = [
        { value: 'promoteur-immobilieres', viewValue: 'Promoteur Immobilier' },
        { value: 'materiaux-construction', viewValue: 'Matériaux de Construction' },
        { value: 'agence-immobilieres', viewValue: 'Agence Immobilière' },
        { value: 'agence-service', viewValue: 'Agence de Service' },
        { value: 'meilleurs-biens', viewValue: 'Meilleurs Biens' },
        { value: 'cafe-restaurant', viewValue: 'Café-Restaurant' }
    ];

    regions: { value: string; viewValue: string }[] = VILLES.map(ville => ({
        value: ville.id,
        viewValue: ville.label
    }));

    constructions: { value: string; viewValue: string }[] = MATERIAUX.map(materiel => ({
        value: materiel.value,
        viewValue: materiel.label
    }));

    CategorieServices: { value: string; viewValue: string }[] = AGENCES_SERVICES.map(service => ({
        value: service.value,
        viewValue: service.label
    }));

    sousCategorieServices: { value: string; viewValue: string }[] = [
        { value: 'leasing', viewValue: 'Leasing' },
        { value: 'SocIndustriel', viewValue: 'Sociétés Industrielles' },
        { value: 'servicesDivers', viewValue: 'Services Divers' }
    ];

    constructor(
        private agenciesService: AgenciesService,
        private location: Location,
        private router: Router,
        private awsService: AwscontrolService
    ) {
        this.agencieData = {};
    }

    ngOnInit(): void {
        this.initializeForm();
        this.checkAwsConnection();
    }

    checkAwsConnection(): void {
        this.awsService.checkHealth().subscribe({
            next: (response) => console.log('✅ AWS S3 Connection Healthy:', response),
            error: (err) => console.warn('⚠️ AWS S3 Connection Warning:', err)
        });
    }

    initializeForm(): void {
        this.agencieData = {
            name: '',
            email: '',
            description: '',
            descriptionArab: '',
            descriptionAnglais: '',
            region: '',
            role: '',
            adresse: '',
            categorie: '',
            sous_categorie: '',
            website_url: '',
            facebook_url: '',
            instagram_url: '',
            linkedin_url: '',
            twitter_url: '',
            tiktok_url: '',
            phone_number: '',
            phone_number2: '',
            phone_number_commercial: '',
            videoUrl: '',
            videoUrl2: '',
            logo_url: '',
            coffee: 'no',
            enableFacebookShare: true,
            enableWhatsAppShare: true,
            enableMessengerShare: true,
            defaultShareMessage: '',
            agencyPublicUrl: '',
            publicShareMessage: '',
            subdomain: '',
            availableLanguages: ['FR']
        };
        this.subdomainPreview = '';
        this.currentImage = null;
        this.uploadedImages = [];
        // Réinitialiser les langues : FR coché par défaut
        this.allLanguages.forEach(lang => lang.checked = (lang.code === 'FR'));
        if (this.fileInput) {
            this.fileInput.nativeElement.value = '';
        }
    }

    // ----- Gestion des langues (identique à modifier) -----
    getSelectedLanguagesArray(): string[] {
        return this.allLanguages.filter(lang => lang.checked).map(lang => lang.code);
    }

    onLanguageChange(): void {
        // S'assurer qu'au moins une langue est cochée
        if (!this.hasAnyLanguageSelected) {
            const frLang = this.allLanguages.find(l => l.code === 'FR');
            if (frLang) frLang.checked = true;
        }
        this.agencieData.availableLanguages = this.getSelectedLanguagesArray();
    }

    showDescriptionField(langCode: string): boolean {
        const lang = this.allLanguages.find(l => l.code === langCode);
        return lang ? lang.checked : false;
    }

    // ----- Sous‑domaine -----
    getSubdomainPreview(): string {
        if (this.agencieData.subdomain && this.agencieData.subdomain.trim()) {
            return `https://${this.agencieData.subdomain.trim().toLowerCase()}.tunimmob.com`;
        }
        return '';
    }

    onSubdomainChange(): void {
        this.subdomainPreview = this.getSubdomainPreview();
    }

    // ----- Gestion des rôles et catégories -----
    onRoleChange(): void {
        this.agencieData.categorie = '';
        this.agencieData.sous_categorie = '';
        this.agencieData.coffee = 'no';
    }

    onCategorieChange(): void {
        if (this.agencieData.categorie !== 'servicesDivers') {
            this.agencieData.sous_categorie = '';
        }
    }

    showCategoryField(): boolean {
        return this.agencieData.role === 'materiaux-construction' || 
               this.agencieData.role === 'agence-service';
    }

    showSousCategorieField(): boolean {
        return this.agencieData.role === 'agence-service' && 
               this.agencieData.categorie === 'servicesDivers';
    }

    // ----- Formatage du texte (identique) -----
    formatText(command: string, textareaRef: ElementRef): void {
        if (textareaRef && textareaRef.nativeElement) {
            const textarea = textareaRef.nativeElement;
            const start = textarea.selectionStart;
            const end = textarea.selectionEnd;
            let fieldName = '';
            if (textareaRef === this.descriptionTextarea) {
                fieldName = 'description';
            } else if (textareaRef === this.descriptionArabTextarea) {
                fieldName = 'descriptionArab';
            } else if (textareaRef === this.descriptionAnglaisTextarea) {
                fieldName = 'descriptionAnglais';
            }
            const currentText = this.agencieData[fieldName] || '';
            const selectedText = currentText.substring(start, end);
            if (!selectedText) return;
            let formattedText = '';
            let tag = '';
            switch (command) {
                case 'bold': tag = 'strong'; break;
                case 'italic': tag = 'em'; break;
                case 'underline': tag = 'u'; break;
            }
            formattedText = `<${tag}>${selectedText}</${tag}>`;
            const newText = currentText.substring(0, start) + formattedText + currentText.substring(end);
            this.agencieData[fieldName] = newText;
            setTimeout(() => {
                textarea.focus();
                const newPosition = start + formattedText.length;
                textarea.setSelectionRange(newPosition, newPosition);
            }, 0);
        }
    }

    onInputChange(): void {
        if (this.agencieData.name) {
            this.agencieData.name = this.agencieData.name.replace(/[^A-Z0-9\s]/gi, '').toUpperCase();
        }
    }

    // ----- Logo upload (identique) -----
    uploadimg2(): void {
        if (!this.selectedFiles1 || this.selectedFiles1.length === 0) return;
        if (this.selectedFiles1.length > 1) {
            Swal.fire({ icon: "warning", title: "Attention", text: "Veuillez sélectionner un seul fichier pour le logo.", timer: 3000, showConfirmButton: false });
            return;
        }
        const file = this.selectedFiles1[0];
        if (file.size > 2 * 1024 * 1024) {
            Swal.fire({ icon: "error", title: "Fichier trop volumineux", text: "La taille maximale autorisée est de 2MB.", timer: 3000, showConfirmButton: false });
            return;
        }
        if (!file.type.match(/image\/(jpeg|png|jpg|gif|webp)/)) {
            Swal.fire({ icon: "error", title: "Format non supporté", text: "Formats acceptés: JPEG, PNG, JPG, GIF, WEBP", timer: 3000, showConfirmButton: false });
            return;
        }
        this.uploadLogoToAws(file);
    }

    async uploadLogoToAws(file: File): Promise<void> {
        this.isUploadingLogo = true;
        try {
            Swal.fire({ title: 'Téléchargement en cours...', html: 'Upload du logo vers le serveur', allowOutsideClick: false, showConfirmButton: false, willOpen: () => Swal.showLoading() });
            const folder = 'agency-logos';
            const response = await this.awsService.uploadFile(file, folder).toPromise();
            Swal.close();
            if (response.success && response.urls && response.urls.length > 0) {
                const uploadedUrl = response.urls[0];
                this.uploadedImages = [uploadedUrl];
                this.currentImage = uploadedUrl;
                this.agencieData.logo_url = uploadedUrl;
                this.isLogoChanged = true;
                Swal.fire({ icon: "success", title: "Succès", text: "Logo téléchargé avec succès", timer: 2000, showConfirmButton: false });
            } else {
                throw new Error(response.error || 'Upload failed');
            }
        } catch (err: any) {
            console.error('Upload error:', err);
            let errorMessage = "Impossible de télécharger le logo";
            if (err.message?.includes('timeout')) errorMessage = "Délai d'attente dépassé. Veuillez réessayer.";
            else if (err.status === 413) errorMessage = "Fichier trop volumineux pour le serveur.";
            else if (err.status === 0) errorMessage = "Impossible de se connecter au serveur. Vérifiez votre connexion.";
            Swal.fire({ icon: "error", title: "Échec", text: errorMessage });
        } finally {
            this.isUploadingLogo = false;
        }
    }

    selectFileimg2(event: Event): void {
        const input = event.target as HTMLInputElement;
        if (input.files && input.files.length > 0) {
            this.selectedFiles1 = input.files;
            this.uploadimg2();
        }
    }

    async removeImage(): Promise<void> {
        if (!this.currentImage) return;
        const result = await Swal.fire({ title: 'Supprimer le logo?', text: "Cette action ne peut pas être annulée", icon: 'warning', showCancelButton: true, confirmButtonColor: '#dc3545', cancelButtonColor: '#6c757d', confirmButtonText: 'Oui, supprimer', cancelButtonText: 'Annuler', reverseButtons: true });
        if (result.isConfirmed) {
            try {
                if (this.currentImage) await this.awsService.deleteFile(this.currentImage).toPromise();
            } catch (error) { console.warn('Could not delete logo from AWS:', error); }
            this.currentImage = null;
            this.agencieData.logo_url = '';
            this.uploadedImages = [];
            this.isLogoChanged = true;
            if (this.fileInput) this.fileInput.nativeElement.value = '';
            Swal.fire({ icon: "success", title: "Supprimé", text: "Logo supprimé avec succès", timer: 2000, showConfirmButton: false });
        }
    }

    // ----- Reel video (identique) -----
    onReelFileSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        if (input.files && input.files.length > 0) {
            const file = input.files[0];
            if (!file.type.startsWith('video/')) {
                Swal.fire({ icon: 'error', title: 'Format non supporté', text: 'Veuillez sélectionner un fichier vidéo (MP4, MOV, AVI)', timer: 3000, showConfirmButton: false });
                return;
            }
            if (file.size > 100 * 1024 * 1024) {
                Swal.fire({ icon: 'error', title: 'Fichier trop volumineux', text: 'La taille maximale autorisée est de 100MB.', timer: 3000, showConfirmButton: false });
                return;
            }
            this.selectedReelFile = file;
            this.selectedReelFileName = file.name;
            this.reelFileChanged = true;
            this.uploadReelVideo();
        }
    }

    async uploadReelVideo(): Promise<void> {
        if (!this.selectedReelFile) return;
        this.isUploadingReel = true;
        try {
            Swal.fire({ title: 'Téléchargement du reel...', text: 'Veuillez patienter', allowOutsideClick: false, showConfirmButton: false, willOpen: () => Swal.showLoading() });
            const folder = 'Videos';
            const response = await this.awsService.uploadFile(this.selectedReelFile, folder).toPromise();
            Swal.close();
            if (response.success && response.urls && response.urls.length > 0) {
                this.agencieData.twitter_url = response.urls[0];
                this.reelUploadedUrl = response.urls[0];
                this.reelFileChanged = true;
                Swal.fire({ icon: 'success', title: 'Succès', text: 'Reel vidéo téléchargé avec succès', timer: 2000, showConfirmButton: false });
            } else {
                throw new Error(response.error || 'Upload failed');
            }
        } catch (err: any) {
            console.error('Reel upload error:', err);
            Swal.fire({ icon: 'error', title: 'Échec du téléchargement', text: err.message || 'Impossible de télécharger le fichier' });
            this.clearReelFile();
        } finally {
            this.isUploadingReel = false;
        }
    }

    clearReelFile(): void {
        this.selectedReelFile = null;
        this.selectedReelFileName = '';
        this.reelFileChanged = false;
        this.reelUploadedUrl = null;
        const fileInput = document.querySelector('#reelFileInput') as HTMLInputElement;
        if (fileInput) fileInput.value = '';
    }

    clearReelUrl(): void {
        this.agencieData.twitter_url = '';
        this.reelFileChanged = true;
        this.clearReelFile();
    }

    getReelFileName(): string {
        if (this.agencieData.twitter_url) {
            const parts = this.agencieData.twitter_url.split('/');
            return parts[parts.length - 1] || 'Fichier vidéo';
        }
        return '';
    }

    // ----- Submit (création) -----
    onSubmit(): void {
        if (!this.agencieData.name || this.agencieData.name.trim() === '') {
            Swal.fire({ icon: "warning", title: "Champ requis", text: "Le nom est obligatoire." });
            return;
        }
        if (!this.agencieData.email || this.agencieData.email.trim() === '') {
            Swal.fire({ icon: "warning", title: "Champ requis", text: "L'email est obligatoire." });
            return;
        }
        if (!this.agencieData.role || this.agencieData.role.trim() === '') {
            Swal.fire({ icon: "warning", title: "Champ requis", text: "Le type de société est obligatoire." });
            return;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(this.agencieData.email)) {
            Swal.fire({ icon: "error", title: "Email invalide", text: "Veuillez entrer une adresse email valide." });
            return;
        }

        if (this.isUploadingLogo || this.isUploadingReel) {
            Swal.fire({ icon: 'warning', title: 'Téléchargement en cours', text: 'Veuillez attendre la fin du téléchargement', confirmButtonColor: '#ffc107' });
            return;
        }

        this.isLoading = true;
        const availableLanguages = this.getSelectedLanguagesArray();

        const agencyData: any = {
            name: this.agencieData.name.toUpperCase().trim(),
            email: this.agencieData.email.toLowerCase().trim(),
            role: this.agencieData.role,
            description: this.agencieData.description?.trim() || '',
            descriptionArab: this.agencieData.descriptionArab?.trim() || '',
            descriptionAnglais: this.agencieData.descriptionAnglais?.trim() || '',
            availableLanguages: availableLanguages,
            region: this.agencieData.region || '',
            adresse: this.agencieData.adresse?.trim() || '',
            phone_number: this.agencieData.phone_number || null,
            phone_number2: this.agencieData.phone_number2 || null,
            phone_number_commercial: this.agencieData.phone_number_commercial || null,
            logo_url: this.agencieData.logo_url || '',
            subdomain: this.agencieData.subdomain?.trim()?.toLowerCase() || null,
        };

        if (this.showCategoryField()) {
            agencyData.categorie = this.agencieData.categorie || '';
            agencyData.sous_categorie = this.agencieData.sous_categorie || '';
        }

        if (this.agencieData.website_url?.trim()) agencyData.website_url = this.agencieData.website_url.trim();
        if (this.agencieData.facebook_url?.trim()) agencyData.facebook_url = this.agencieData.facebook_url.trim();
        if (this.agencieData.instagram_url?.trim()) agencyData.instagram_url = this.agencieData.instagram_url.trim();
        if (this.agencieData.linkedin_url?.trim()) agencyData.linkedin_url = this.agencieData.linkedin_url.trim();
        if (this.agencieData.twitter_url?.trim()) agencyData.twitter_url = this.agencieData.twitter_url.trim();
        if (this.agencieData.tiktok_url?.trim()) agencyData.tiktok_url = this.agencieData.tiktok_url.trim();
        if (this.agencieData.videoUrl?.trim()) agencyData.videoUrl = this.agencieData.videoUrl.trim();
        if (this.agencieData.videoUrl2?.trim()) agencyData.videoUrl2 = this.agencieData.videoUrl2.trim();

        console.log('Creating agency with data:', agencyData);

        this.agenciesService.createItem(agencyData).subscribe(
            (response: any) => {
                this.isLoading = false;
                let message = 'Société ajoutée avec succès!';
                if (typeof response === 'string') message = response;
                else if (response && response.message) message = response.message;

                Swal.fire({
                    icon: "success",
                    title: "Succès!",
                    text: message,
                    showConfirmButton: true,
                    confirmButtonColor: '#4caf50',
                    timer: 3000
                }).then(() => {
                    this.initializeForm();
                    this.location.back();
                });
            },
            (error: HttpErrorResponse) => {
                this.isLoading = false;
                console.error('Full error object:', error);
                let errorMessage = "Une erreur est survenue lors de l'ajout de la société.";
                let errorTitle = "Erreur";

                if (error.status === 403) {
                    errorTitle = "Accès refusé";
                    errorMessage = "Vous n'avez pas les permissions nécessaires.";
                } else if (error.status === 401) {
                    errorTitle = "Non authentifié";
                    errorMessage = "Votre session a expiré. Veuillez vous reconnecter.";
                } else if (error.error) {
                    if (typeof error.error === 'string') errorMessage = error.error;
                    else if (error.error.message) {
                        errorMessage = error.error.message;
                        if (error.error.message.includes("User exist")) {
                            errorTitle = "Email déjà utilisé";
                            errorMessage = "Un utilisateur avec cet email existe déjà.";
                        }
                    }
                } else if (error.status === 0) {
                    errorMessage = "Impossible de se connecter au serveur.";
                } else if (error.status === 500) {
                    errorMessage = "Erreur interne du serveur. Veuillez réessayer plus tard.";
                }

                Swal.fire({
                    icon: "error",
                    title: errorTitle,
                    html: `<p>${errorMessage}</p><small>Code: ${error.status}</small>`,
                    confirmButtonColor: '#f44336'
                });
            }
        );
    }

    // ----- Annulation -----
    cancel(): void {
        this.location.back();
    }

    // ----- Réinitialisation -----
    resetForm(): void {
        Swal.fire({
            title: 'Réinitialiser le formulaire ?',
            text: 'Toutes les données seront effacées.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#f44336',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Oui',
            cancelButtonText: 'Annuler'
        }).then((result) => {
            if (result.isConfirmed) {
                this.initializeForm();
            }
        });
    }
}